'use client';

import api from '@/lib/api';
import { t } from '@/lib/i18n';
import { useAuthStore } from '@/store/auth.store';
import { useRouter } from 'next/navigation';
import { FormEvent, useState } from 'react';

export default function LoginPage() {
  const router = useRouter();
  const { setAuth } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/api/auth/login', { email, password });
      const { user, tokens } = data.data;
      localStorage.setItem('accessToken', tokens.accessToken);
      localStorage.setItem('refreshToken', tokens.refreshToken);
      setAuth(user, tokens);
      router.push('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || t.loginFailed.en);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-600 to-teal-700 p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Brand header */}
        <div className="bg-emerald-600 px-8 pt-8 pb-6 text-center text-white">
          <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center text-5xl mx-auto mb-4 shadow-lg">
            🛒
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight">RashanPro</h1>
          <p className="text-urdu text-emerald-100 text-xl mt-1">سستا راشن</p>
          <p className="text-emerald-200 text-sm mt-2">Smart Store Manager · ہوشمند دکان مینیجر</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-5 sm:px-8 py-6 space-y-5">
          <div className="text-center mb-2">
            <h2 className="text-lg font-bold text-gray-800">{t.login.en}</h2>
            <p className="text-urdu text-gray-400 text-sm">{t.login.ur}</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              {t.email.en} <span className="text-urdu text-xs text-gray-400">{t.email.ur}</span>
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@rashanpro.com"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 text-base touch-target"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              {t.password.en} <span className="text-urdu text-xs text-gray-400">{t.password.ur}</span>
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 text-base touch-target"
            />
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 text-sm px-4 py-3 rounded-xl border border-red-200 flex items-center gap-2">
              ❌ {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white font-bold rounded-xl transition-colors text-base shadow-sm touch-target"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="animate-spin">⏳</span> {t.loading.en}
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                {t.loginBtn.en} <span className="text-urdu opacity-80 text-sm">{t.loginBtn.ur}</span>
              </span>
            )}
          </button>

          {/* Quick-fill credentials (dev) */}
          <div className="pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-400 text-center mb-2">Quick fill (dev only)</p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: '👑 Super Admin', email: 'superadmin@rashanapp.pk', pwd: 'SuperAdmin@123' },
                { label: '🛒 Kiryana', email: 'admin.kiryana@rashanapp.pk', pwd: 'StoreAdmin@123' },
                { label: '🍽️ Restaurant', email: 'admin.restaurant@rashanapp.pk', pwd: 'StoreAdmin@123' },
                { label: '🥐 Bakery', email: 'admin.bakery@rashanapp.pk', pwd: 'StoreAdmin@123' },
                { label: '💊 Pharmacy', email: 'admin.pharmacy@rashanapp.pk', pwd: 'StoreAdmin@123' },
                { label: '🏪 General', email: 'admin.general@rashanapp.pk', pwd: 'StoreAdmin@123' },
              ].map((cred) => (
                <button
                  key={cred.label}
                  type="button"
                  onClick={() => { setEmail(cred.email); setPassword(cred.pwd); }}
                  className="text-xs px-3 py-2 bg-gray-100 hover:bg-emerald-50 hover:text-emerald-700 rounded-lg text-gray-600 transition-colors font-medium"
                >
                  {cred.label}
                </button>
              ))}
            </div>
          </div>
        </form>

        <div className="px-8 pb-6 text-center">
          <p className="text-xs text-gray-400">rashanpro.zeonedge.com · راشن پرو</p>
        </div>
      </div>
    </div>
  );
}

